import React from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip
} from '@mui/material';
import {
  AttachMoney
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * TransactionCostCard Component
 * 
 * Displays transaction cost analysis for portfolio trades:
 * - Summary metrics (total cost, avg per trade, num trades)
 * - Per-ticker cost breakdown
 * - Color-coded cost levels
 * - Net exposure calculation
 * 
 * NEW - Phase 1 Day 3: Transaction Cost Visualization
 * 
 * @param {Object} transactionCosts - Transaction cost data from API
 * @param {number} exposure - Gross portfolio exposure
 * @param {number} netExposure - Net exposure after costs
 */
const TransactionCostCard = ({ transactionCosts, exposure, netExposure }) => {
  const { t } = useTranslation();

  if (!transactionCosts) return null;

  const {
    total_cost_vnd = 0,
    total_cost_pct = 0,
    avg_cost_per_trade = 0,
    num_trades = 0,
    trades = []
  } = transactionCosts;

  // Helper: Format VND currency
  const formatVND = (value) => {
    if (value >= 1_000_000_000) {
      return `${(value / 1_000_000_000).toFixed(2)}B`;
    } else if (value >= 1_000_000) {
      return `${(value / 1_000_000).toFixed(2)}M`;
    } else if (value >= 1_000) {
      return `${(value / 1_000).toFixed(2)}K`;
    }
    return value.toFixed(0);
  };

  // Helper: Get cost level color
  const getCostColor = (costPct) => {
    if (costPct < 0.005) return 'success'; // < 0.5% = green
    if (costPct < 0.01) return 'warning';  // < 1% = yellow
    return 'error';                         // >= 1% = red
  };

  // Helper: Get cost level label
  const getCostLabel = (costPct) => {
    if (costPct < 0.005) return t('transactionCosts.costLevels.low');
    if (costPct < 0.01) return t('transactionCosts.costLevels.medium');
    return t('transactionCosts.costLevels.high');
  };

  return (
    <Card sx={{ p: 2.5, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <AttachMoney sx={{ color: 'warning.main', fontSize: 28 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem' }}>
            {t('transactionCosts.title')}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
            {num_trades} {t('transactionCosts.metrics.numTrades')}
          </Typography>
        </Box>
        <Chip
          label={getCostLabel(total_cost_pct)}
          color={getCostColor(total_cost_pct)}
          size="small"
          sx={{ fontWeight: 600, fontSize: '0.7rem', height: 24 }}
        />
      </Box>

      {/* Summary Metrics - Compact Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5, mb: 2 }}>
        {/* Total Cost % */}
        <Tooltip title={t('transactionCosts.tooltips.totalCostPct')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('transactionCosts.metrics.totalCostPct')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main', fontSize: '1.1rem' }}>
              {(total_cost_pct * 100).toFixed(2)}%
            </Typography>
          </Box>
        </Tooltip>

        {/* Total Cost VND */}
        <Tooltip title={t('transactionCosts.tooltips.totalCostVnd')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              {t('transactionCosts.metrics.totalCostVnd')}
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {formatVND(total_cost_vnd)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Avg Cost per Trade */}
        <Tooltip title={t('transactionCosts.tooltips.avgCostPerTrade')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              TB/Giao Dịch
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {formatVND(avg_cost_per_trade)}
            </Typography>
          </Box>
        </Tooltip>

        {/* Number of Trades */}
        <Tooltip title={t('transactionCosts.tooltips.numTrades')} arrow>
          <Box sx={{ p: 1.5, bgcolor: 'background.default', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem', display: 'block', mb: 0.5 }}>
              Số Giao Dịch
            </Typography>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
              {num_trades}
            </Typography>
          </Box>
        </Tooltip>
      </Box>

      {/* Net Exposure Impact - Compact */}
      <Box sx={{
        p: 1.5,
        bgcolor: 'info.lighter',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'info.light',
        mb: 2,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}>
        <Typography variant="caption" sx={{ color: 'info.dark', fontWeight: 600, fontSize: '0.7rem' }}>
          Exposure:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
            {(exposure * 100).toFixed(1)}%
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>−</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'error.main', fontSize: '0.85rem' }}>
            {(total_cost_pct * 100).toFixed(2)}%
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>=</Typography>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.main', fontSize: '0.85rem' }}>
            {(netExposure * 100).toFixed(1)}%
          </Typography>
        </Box>
      </Box>

      {/* Per-Ticker Breakdown */}
      {trades && trades.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, fontSize: '0.85rem' }}>
            {t('transactionCosts.breakdown.title')}
          </Typography>
          <TableContainer sx={{ maxHeight: 250 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                      {t('transactionCosts.breakdown.ticker')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                      {t('transactionCosts.breakdown.orderValue')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                      {t('transactionCosts.breakdown.slippage')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                      {t('transactionCosts.breakdown.commission')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                      {t('transactionCosts.breakdown.spread')}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.7rem', py: 1 }}>
                      {t('transactionCosts.breakdown.totalCost')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {trades.map((trade, idx) => (
                    <TableRow key={idx} hover>
                      <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', py: 0.75 }}>
                        {trade.ticker}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                        {formatVND(trade.order_value)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                        {(trade.slippage_pct * 100).toFixed(2)}%
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                        {formatVND(trade.commission_vnd)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                        {formatVND(trade.spread_vnd)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: '0.7rem', py: 0.75 }}>
                        <Chip
                          label={`${(trade.total_cost_pct * 100).toFixed(2)}%`}
                          color={getCostColor(trade.total_cost_pct)}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          </TableContainer>
        </>
      )}
    </Card>
  );
};

export default TransactionCostCard;
